import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredArticles =
    activeSlug === 'all'
      ? articles
      : articles.filter((a) => a.category === activeSlug);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: currentCategory.name },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
              <i className={`${currentCategory.icon} text-lg text-primary-600`}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500">{currentCategory.description}</p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 pb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => {
                  setActiveSlug(cat.slug);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline flex-shrink-0 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full px-4 md:px-6 py-6">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - cy社区`,
            description: currentCategory.description,
            url: `https://cycommunity.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: 'cy社区',
              url: 'https://cycommunity.com',
            },
          }),
        }}
      />
    </>
  );
}